import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { adminApi, ApiError } from '@l2u/ui/lib/api.ts';
import { getAdminSupabase } from '@l2u/ui/lib/supabase.ts';
import type { AdminUser } from '@l2u/ui/lib/types.ts';
import { setAdminAccessToken } from '@l2u/ui/lib/auth/adminToken.ts';
import { readDenyReason, type DenyReason } from './denyReason.ts';
import { clearSessionStart, markSessionStart } from './sessionTimeout.ts';
import { singleFlightByKey } from './singleFlight.ts';
import { forgetSsoUser, markSsoAttempted, safeStorage } from './ssoHint.ts';

type AdminAuthValue = {
  user: AdminUser | null;
  session: Session | null;
  loading: boolean;
  // Set when the API refused an authenticated user (403). Distinct from
  // `user === null`, which also covers "not signed in" — the two need opposite
  // treatment, so they cannot share a flag.
  accessDenied: { reason: DenyReason } | null;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signOut: (opts?: { automatic?: boolean }) => Promise<void>;
  reload: () => Promise<void>;
  /**
   * The access check for this token — the SAME GET /api/auth/admin/me the
   * provider runs for its auth events, shared rather than repeated. Resolves
   * with the verdict; rejects with the ApiError for a 401, a network error or
   * a 500 (not verdicts). Used by the SSO landing page, which must decide
   * where to send the user while auth-js's SIGNED_IN starts the same check.
   */
  checkAccess: (accessToken: string) => Promise<AccessCheck>;
};

export type AccessCheck =
  | { status: 'ok'; user: AdminUser }
  | { status: 'denied'; reason: DenyReason };

const AdminAuthContext = createContext<AdminAuthValue>({
  user: null,
  session: null,
  loading: true,
  accessDenied: null,
  signInWithPassword: async () => {},
  signOut: async () => {},
  reload: async () => {},
  checkAccess: () => Promise.reject(new Error('AdminAuthProvider is not mounted')),
});

export function AdminAuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState<{ reason: DenyReason } | null>(null);

  // One GET /api/auth/admin/me per access token. On every page load auth-js
  // reports the same session three times (getSession, the SIGNED_IN its
  // session recovery emits, INITIAL_SESSION), and each used to ask the API
  // again; Chrome then queued the identical GETs one behind another (~2 s).
  // A new token (sign-in, refresh) still asks; reload() always asks.
  // useState initializer, not useMemo: React guarantees to keep this instance.
  const [fetchUserOnce] = useState(() =>
    singleFlightByKey<AccessCheck>(async (_token, apply) => {
      // Every write goes through apply(): an answer older than one already
      // written, or one started before a sign-out, is dropped (singleFlight.ts).
      try {
        const res = await adminApi.get<{ user: AdminUser }>('/api/auth/admin/me');
        apply(() => {
          setAccessDenied(null);
          setUser(res.user);
        });
        return { status: 'ok', user: res.user };
      } catch (err) {
        // 403 means the person is authenticated but refused, so the session is
        // deliberately KEPT. Signing out here would clear only THIS portal's
        // session while the central l2u-identity and Okta sessions survive — the
        // next sign-in click would round-trip silently and refuse again, a fast
        // bounce loop with no message (spec S10). Holding the session is also
        // what lets the refusal screen name who is signed in and offer a real
        // sign-out.
        if (err instanceof ApiError && err.status === 403) {
          const reason = readDenyReason(err.details);
          apply(() => {
            setAccessDenied({ reason });
            setUser(null);
          });
          return { status: 'denied', reason };
        }
        // 401 means the Supabase session itself is not usable. There is nothing
        // to preserve, so clear it rather than leaving the user half signed in.
        if (err instanceof ApiError && err.status === 401) {
          try {
            await getAdminSupabase().auth.signOut();
          } catch {
            // ignore
          }
        }
        // A 401 or anything else (a network blip, a 500) is not a refusal. Leave
        // the flag clear so the user lands on the login page rather than being
        // told they have no access when the truth is that the request never
        // landed. Written as a non-verdict: it must not block an older check's
        // real answer that is still on its way. apply() also re-checks after the
        // awaited signOut above, so a newer session's answer is never clobbered.
        apply(
          () => {
            setAccessDenied(null);
            setUser(null);
          },
          { verdict: false },
        );
        // Neither is a verdict to remember for this token. The API answers 401
        // when it cannot reach Supabase, and auth-js then keeps the session
        // (its signOut fails the same way, so no SIGNED_OUT and no reset):
        // remembering it would pin the admin to the login page until the token
        // refreshes, where the next tab-focus SIGNED_IN used to self-heal.
        // Rethrowing makes singleFlightByKey forget it; loadUser swallows it.
        throw err;
      }
    }),
  );

  const loadUser = useCallback(
    async (currentSession: Session | null, opts?: { force?: boolean }) => {
      if (!currentSession) {
        // No session means nothing has been refused. Clearing the flag here is
        // what stops a signed-out user being bounced back to /no-access by
        // AdminShell's guard when they return to the app.
        fetchUserOnce.reset();
        setAccessDenied(null);
        setUser(null);
        return;
      }
      try {
        await fetchUserOnce(currentSession.access_token, opts);
      } catch {
        // Already handled inside (user cleared, not marked denied); swallowed so
        // no caller sees a rejection, exactly as before.
      }
    },
    [fetchUserOnce],
  );

  useEffect(() => {
    let mounted = true;
    const supabase = (() => {
      try {
        return getAdminSupabase();
      } catch {
        return null;
      }
    })();

    if (!supabase) {
      setLoading(false);
      return () => {};
    }

    void supabase.auth.getSession().then(async ({ data }) => {
      if (!mounted) return;
      const initial = data.session ?? null;
      setSession(initial);
      setAdminAccessToken(initial?.access_token ?? null);
      await loadUser(initial);
      if (mounted) setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((event, next) => {
      if (!mounted) return;
      if (event === 'SIGNED_IN') markSessionStart(Date.now());
      if (event === 'SIGNED_OUT') clearSessionStart();
      setSession(next ?? null);
      setAdminAccessToken(next?.access_token ?? null);
      void loadUser(next ?? null);
    });

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
  }, [loadUser]);

  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      const supabase = getAdminSupabase();
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      setSession(data.session);
      setAdminAccessToken(data.session?.access_token ?? null);
      await loadUser(data.session);
    },
    [loadUser],
  );

  // `automatic: true` marks a sign-out the USER did not choose -- today only the
  // 12-hour absolute session cap, the idle timeout having been removed. It
  // changes exactly one thing: the SSO re-entry hint survives, because a lapsed
  // session is precisely the case that hint exists for. Clearing it there would
  // undo the feature in its commonest path. Everything else, including ending
  // the central identity session, is identical, and omitting the argument lands
  // on the safe clearing behaviour.
  //
  // Note this is now the ONLY automatic path. An inactivity lapse is enforced
  // server-side and never reaches this function at all: the refresh is simply
  // refused, auth-js emits SIGNED_OUT on its own, and no marker is set -- which
  // is what leaves re-entry hands-free after a lapse but not after the cap.
  const signOut = useCallback(
    async (opts?: { automatic?: boolean }) => {
      // Decided FIRST, before the awaited network logout, because AdminShell and
      // AdminNoAccess both navigate to the login page BEFORE awaiting this. Left in
      // the finally, AdminLogin mounted with the hint still set, auto-started SSO,
      // and raced the central-logout navigation -- and if it won, the identity
      // session was never ended and stale Okta claims lived on.
      if (opts?.automatic) {
        // A timeout keeps the hint but must not auto-start into the logout that is
        // already in flight. Cleared by the next completed sign-in.
        markSsoAttempted(safeStorage(() => window.sessionStorage));
      } else {
        forgetSsoUser(safeStorage(() => window.localStorage));
      }
      try {
        const supabase = getAdminSupabase();
        await supabase.auth.signOut();
      } finally {
        // Not left to the SIGNED_OUT event: when auth-js's signOut fails (an
        // outage) no event fires, and the token would stay remembered.
        fetchUserOnce.reset();
        setSession(null);
        setUser(null);
        // Cleared explicitly rather than relying on the SIGNED_OUT event, so a
        // sign-out taken FROM the refusal screen cannot leave a stale flag that
        // redirects the next visit straight back to /no-access.
        setAccessDenied(null);
        setAdminAccessToken(null);
        clearSessionStart();
      }
    },
    [fetchUserOnce],
  );

  // Always asks again, even for the same token: used after the admin changes
  // their own role and by the no-access screen's "check again".
  const reload = useCallback(async () => {
    await loadUser(session, { force: true });
  }, [loadUser, session]);

  const checkAccess = useCallback(
    (accessToken: string) => fetchUserOnce(accessToken),
    [fetchUserOnce],
  );

  const value = useMemo<AdminAuthValue>(
    () => ({
      user,
      session,
      loading,
      accessDenied,
      signInWithPassword,
      signOut,
      reload,
      checkAccess,
    }),
    [user, session, loading, accessDenied, signInWithPassword, signOut, reload, checkAccess],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth(): AdminAuthValue {
  return useContext(AdminAuthContext);
}
